/** The Sentry environments the browser apps report under, one per kind of deployment. */
export type BrowserSentryEnvironment = 'production' | 'staging' | 'bytecrate';

/**
 * The deployments that report to Sentry, by the site's domain. A host is the domain itself or any name under it, so
 * team hosts (`<team>.stitch.ai`, `<team>.bytecrate.me`) report with their site.
 */
const ENVIRONMENT_DOMAINS: ReadonlyArray<readonly [domain: string, environment: BrowserSentryEnvironment]> = [
  ['stitch.ai', 'production'],
  ['alphastitch.ai', 'staging'],
  ['bytecrate.me', 'bytecrate'],
] as const;

/**
 * The Sentry environment of the page's host, or null for any other host, where Sentry must not start at all.
 *
 * The same production build serves every deployment, so this is decided from the address at run time, not at build
 * time. Local and agent builds (`localhost`, `*.local`, IP addresses) and unknown hosts get null: their errors are
 * not ours to triage and used to land in production.
 */
export function sentryEnvironmentForHost(hostname: string): BrowserSentryEnvironment | null {
  const host = hostname.trim().toLowerCase().replace(/\.$/, '');
  const match = ENVIRONMENT_DOMAINS.find(([domain]) => host === domain || host.endsWith(`.${domain}`));
  return match ? match[1] : null;
}
